import React, { useState } from 'react'
import { Button, Heading, Message, Text, useToast } from '@afterpaytouch/core'
import { Trans } from 'react-i18next'

import { CheckoutPage, Weekday } from '../../model'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { Head, ModuleContainer } from '../../component'
import { PaymentDayList } from '../../component/PaymentDayList'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import styles from './styles.module.css'
import { useAppRouter, useCheckoutToken, useProfilingId, useSelectedPreferredDay, useUpdatePreferredPaydayMutation } from '../../state'
import { TrackingEvent } from '../../model/monitoring'
import { getLocaleKeyFromDayNumber, getUppercaseDayNameFromDayNumber } from '../../utils/calendar'
import { Route } from '../../config/router'

export const PreferredPaymentDayPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { headingSize, textSize } = useCheckoutSizes()
  const defaultSelectedDay = useSelectedPreferredDay()
  const [selectedDay, setSelectedDay] = useState(defaultSelectedDay)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [updatePreferredPayday, { isLoading }] = useUpdatePreferredPaydayMutation()
  const setToast = useToast()
  const profilingSessionId = useProfilingId()
  const token = useCheckoutToken()

  const pageTitle = `${t('preferredPaymentDay:setPaymentDay')} | ${t('common:brandName')}`

  const handleSuccess = (): void => {
    logEvent(TrackingEvent.ALIGN_PAYMENT_SAVE_SUCCESS)

    setToast({
      kind: 'Success',
      testNameSpace: 'preferredDay',
      message: () => (
        <Text>
          <Trans
            i18nKey={t('preferredPaymentDay:saveSuccessMessage', { selectedPaymentDay: t(getLocaleKeyFromDayNumber(selectedDay)) })}
            components={{
              b: <strong />,
            }}
          />
        </Text>
      ),
    })
  }

  const handleFailure = (): void => {
    logEvent(TrackingEvent.ALIGN_PAYMENT_SAVE_FAILURE)

    setToast({
      kind: 'Failure',
      message: t('preferredPaymentDay:saveFailureMessage'),
    })
  }

  const handleSave = async (): Promise<void> => {
    logEvent(TrackingEvent.PRESSED_ALIGN_PAYMENT_SAVE_BUTTON)
    try {
      await updatePreferredPayday({ token, profilingSessionId, preferredPaymentDay: getUppercaseDayNameFromDayNumber(selectedDay) }).unwrap()
      handleSuccess()
    } catch (error) {
      handleFailure()
    } finally {
      router.push(Route.SUMMARY)
    }
  }

  const handleListChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSelectedDay(parseInt(e.target.value) as Weekday)
  }

  return (
    <>
      <Head title={pageTitle} />
      <Heading size={headingSize}>{t('preferredPaymentDay:setPaymentDay')}</Heading>
      <Text size={textSize}>{t('preferredPaymentDay:setPaymentDayDescription')}</Text>
      <ModuleContainer padding={{ vertical: 'Small' }}>
        <PaymentDayList onChange={handleListChange} />
      </ModuleContainer>
      <div className={styles.messageContainer}>
        <Message kind='Basic' description={t('preferredPaymentDay:paymentDayInfo')} iconName='Info' />
      </div>
      <Button.Secondary padding='Fluid' onClick={handleSave} loading={isLoading} testNameSpace='preferred-payment-day-page-save'>
        {t('preferredPaymentDay:save')}
      </Button.Secondary>
      <TrackPageViewed />
    </>
  )
}
